/**
 * MELYIK HIÁNYT ZÁRJA LE EZ A PAPÍR — a jelöltek rangsora. (#53)
 *
 * A „Melyik hiányt zárja le?" lista eddig a cég MINDEN nyitott hiánytételét
 * kitette, rendezetlenül. Egy aktív cégnél ez tucatnyi sor, és a könyvelő
 * minden egyes lefűzésnél végigolvasta őket — holott a válasz általában ott
 * van a papíron: ugyanaz a számlaszám, ugyanaz az összeg, ugyanaz a nap.
 *
 * ⚠️ AMIT EZ A FÜGGVÉNY NEM CSINÁL: NEM VÁLASZT. A legjobb jelölt megjelölve
 * áll, de a rádiógomb üres marad *(Viktor, 2026-09-23)*. Egy előre bepipált
 * gombnál a sor-mód `Enter`-e a ROSSZ találatot is lefűzné, és a lefűzés
 * visszafordítása drága — a rangsor a KERESÉST spórolja meg, nem a döntést.
 *
 * ── MIÉRT KÜLÖN FÁJL ────────────────────────────────────────────────────────
 *
 * Mert így mérhető. A pontozás a komponensbe írva csak úgy lenne tesztelhető,
 * hogy felrenderelünk egy munkalistát, és a DOM sorrendjéből következtetünk
 * vissza — a szabály maga sosem lenne állítható. Itt viszont minden szabály
 * egy tétel, és a `candidateRanking.spec.ts` külön-külön szabotálja őket.
 */

import type { DiscrepancyListItem } from '@/features/discrepancies/types';
import type { WorklistItem } from './types';

/**
 * A PONTOK. Minden tétel FÜGGETLEN bizonyíték, ezért összeadódnak.
 *
 * ⚠️ A SZÁMLASZÁM TÖBBET ÉR, MINT AZ ÖSSZES TÖBBI EGYÜTT (100 > 40+20+25).
 * Ez nem hangolás, hanem a döntés lényege: a számlaszám AZONOSÍTÓ, a többi
 * mező csak egybeesés. Két 45 200 Ft-os, azonos napi számla ugyanattól a
 * partnertől mindennapos — két azonos számlaszám ugyanannál a cégnél nem az.
 * Enélkül három gyenge egybeesés leverhetné az igazi találatot.
 */
const PONT = {
  szamlaszam: 100,
  osszeg: 40,
  partnernev: 25,
  partnernevReszleges: 15,
  keltAzonosNap: 20,
  keltKozeli: 10,
} as const;

/** Ennyi napon belül még „közeli" a kelt. */
const KOZELI_NAP = 3;

/**
 * Legfeljebb ennyi jelölt látszik, amíg a könyvelő ki nem nyitja a listát.
 *
 * Három, nem öt *(#68, Viktor 2026-10-19 — a „B” elrendezés)*: a jobb hasábban
 * a jelöltek alatt a Tensoft-kontír és a Tételsorok is helyet kér, és a
 * negyedik-ötödik jelölt ritkán a jó — a „További N jelölt” mögött ott van.
 */
export const LATHATO_JELOLTEK = 3;

export interface RankedCandidates {
  /** A rangsor eleje — legfeljebb `LATHATO_JELOLTEK`, vagy EGY, ha egyértelmű. */
  shown: DiscrepancyListItem[];
  /** Ami a „többi" mögött marad. Üres, ha minden látszik. */
  hidden: DiscrepancyListItem[];
  /**
   * A MEGJELÖLT jelölt — vagy `null`.
   *
   * `null`, ha nincs mire alapozni (senki nem ért el pontot), vagy ha
   * HOLTVERSENY van az élen. Egy megjelölés magabiztos állítás; két egyforma
   * jelöltnél nincs mit állítani, és a találgatás rosszabb a csendnél.
   */
  recommendedId: string | null;
}

/**
 * ÖSSZEHASONLÍTHATÓ ALAK — kisbetű, ékezet nélkül, csak betű és szám.
 *
 * A számlaszámnál az elválasztók esnek ki (`AEK-2026/004182` ↔
 * `AEK2026004182`), a partnernévnél a cégforma írásmódja (`MOL Nyrt.` ↔
 * `MOL NYRT`). Mindkettő ugyanaz a bemeneti zaj: az egyik oldal a NAV
 * bejelentéséből jön, a másik OCR-ből vagy kézi javításból.
 */
function osszehasonlithato(value: string | null): string | null {
  if (value === null) {
    return null;
  }
  const normalized = value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

  return normalized === '' ? null : normalized;
}

/**
 * AZONOS PÉNZÖSSZEG — SZÖVEGEN, NEM LEBEGŐPONTON.
 *
 * A pénz ezen a felületen végig szöveg (`Decimal(18, 2)`), és az is marad:
 * `Number`-re váltva a `0.1 + 0.2` világába lépnénk egy puszta EGYENLŐSÉG
 * kedvéért. A két oldal skálája ráadásul eltérhet (`45200` vs `45200.00`),
 * ezért a tizedesek két jegyre normalizálódnak, és utána string-egyezés dönt.
 */
function azonosOsszeg(a: string | null, b: string | null): boolean {
  const bal = penzAlak(a);
  const jobb = penzAlak(b);

  return bal !== null && jobb !== null && bal === jobb;
}

function penzAlak(value: string | null): string | null {
  if (value === null) {
    return null;
  }
  const tisztitott = value.replace(/\s/g, '');
  if (!/^-?\d+([.,]\d+)?$/.test(tisztitott)) {
    return null;
  }
  const [egesz, tizedes = ''] = tisztitott.replace(',', '.').split('.');
  return `${egesz}.${`${tizedes}00`.slice(0, 2)}`;
}

/** Hány nap a két `YYYY-MM-DD` között — `null`, ha bármelyik hiányzik vagy törött. */
function naptavolsag(a: string | null, b: string | null): number | null {
  if (a === null || b === null) {
    return null;
  }
  const bal = Date.parse(`${a}T00:00:00Z`);
  const jobb = Date.parse(`${b}T00:00:00Z`);
  if (Number.isNaN(bal) || Number.isNaN(jobb)) {
    return null;
  }
  return Math.abs(bal - jobb) / 86_400_000;
}

/**
 * EGY JELÖLT PONTSZÁMA a bizonylathoz mérve.
 *
 * Exportált, mert a tesztje szabályonként állít — és mert a nulla pont
 * önmagában is tény: azt jelenti, hogy EGYETLEN közös mező sem egyezik.
 */
export function scoreCandidate(document: WorklistItem, candidate: DiscrepancyListItem): number {
  let pont = 0;

  const bizonylatSzam = osszehasonlithato(document.invoiceNumber);
  const jeloltSzam = osszehasonlithato(candidate.invoiceNumber);
  if (bizonylatSzam !== null && bizonylatSzam === jeloltSzam) {
    pont += PONT.szamlaszam;
  }

  if (azonosOsszeg(document.grossAmount, candidate.amount)) {
    pont += PONT.osszeg;
  }

  const bizonylatPartner = osszehasonlithato(document.partnerName);
  const jeloltPartner = osszehasonlithato(candidate.partnerName);
  if (bizonylatPartner !== null && jeloltPartner !== null) {
    if (bizonylatPartner === jeloltPartner) {
      pont += PONT.partnernev;
    } else if (
      // A RÖVIDÍTETT CÉGNÉV IS TALÁLAT, de kevesebbet ér: az OCR gyakran
      // levágja a cégformát, a NAV viszont a teljes nevet adja. A puszta
      // tartalmazás önmagában viszont gyenge — „Bau Kft" sok mindenben benne van.
      bizonylatPartner.includes(jeloltPartner) ||
      jeloltPartner.includes(bizonylatPartner)
    ) {
      pont += PONT.partnernevReszleges;
    }
  }

  const tavolsag = naptavolsag(document.issueDate, candidate.issueDate);
  if (tavolsag !== null) {
    if (tavolsag === 0) {
      pont += PONT.keltAzonosNap;
    } else if (tavolsag <= KOZELI_NAP) {
      pont += PONT.keltKozeli;
    }
  }

  return pont;
}

/**
 * A RANGSOR — és az a kérdés, hogy EGY jelöltet mutassunk-e, vagy hármat.
 *
 * ⚠️ AZ „EGYÉRTELMŰ" DEFINÍCIÓJA: a legjobb jelölt ELŐNYE a második előtt
 * legalább annyi, amennyit a számlaszám ér. Mivel ennyi pontot CSAK a
 * számlaszám-egyezés ad, ez a gyakorlatban azt jelenti: pontosan EGY jelölt
 * számlaszáma egyezik a papíréval.
 *
 * Miért az ELŐNY dönt, és nem a puszta pontszám: ha két hiánytételen ugyanaz
 * a számlaszám (sztornó és az eredeti), akkor mindkettő 100 fölött van, és
 * egyiket kitenni a másik nélkül épp a nehéz döntést REJTENÉ el. Ilyenkor a
 * lista marad hármas, és a könyvelő látja mind a kettőt.
 *
 * ⚠️ AZONOS PONTSZÁMNÁL A TÉT DÖNT, nem a bejövő sorrend (#77). A jelöltek a
 * hiánylista tételsorrendjében érkeznek, az pedig cégen belül PARTNER
 * szerint ABC-s — ha a pontozás nem tud dönteni, a látható három az ábécé
 * elejéről jönne, nem a legnagyobb tétből. Itt a kérdés más, mint a
 * hiánylistán: nem „kitől kérjem be", hanem „melyik a legvalószínűbb, amit
 * érdemes előbb megnézni" — ezért a tét rendez, ahogy a #77 előtt is.
 *
 * Azonos tétnél a bejövő sorrend marad — ez az `Array.prototype.sort`
 * stabilitási garanciája a modern futtatókban, és a teszt le is köti.
 */
export function rankCandidates(
  document: WorklistItem,
  candidates: readonly DiscrepancyListItem[]
): RankedCandidates {
  const pontozott = candidates.map((candidate, index) => ({
    candidate,
    pont: scoreCandidate(document, candidate),
    // Fillérben, egészként — ugyanaz az érv, mint a backend `toFiller`-jénél.
    tet: candidate.vatAtRisk === null ? 0 : Math.round(Number(candidate.vatAtRisk) * 100),
    index,
  }));

  const rangsor = [...pontozott].sort(
    (a, b) => b.pont - a.pont || b.tet - a.tet || a.index - b.index
  );

  const legjobb = rangsor[0];
  const masodik = rangsor[1];

  // ⚠️ A NULLA PONTOS JELÖLTET SOSEM JELÖLJÜK MEG. Ha semmi nem egyezik, a
  // sorrend a hiánylistáé, nem a miénk — egy megjelölés ott azt állítaná,
  // hogy tudunk valamit, holott nem tudunk semmit.
  const elony = legjobb === undefined ? 0 : legjobb.pont - (masodik?.pont ?? 0);
  const vanNyertes = legjobb !== undefined && legjobb.pont > 0 && elony > 0;
  const egyertelmu = vanNyertes && elony >= PONT.szamlaszam;

  const meddig = egyertelmu ? 1 : LATHATO_JELOLTEK;
  const sorok = rangsor.map((sor) => sor.candidate);

  return {
    shown: sorok.slice(0, meddig),
    hidden: sorok.slice(meddig),
    recommendedId: vanNyertes ? legjobb.candidate.id : null,
  };
}
